import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import AbortIcon from '@material-ui/icons/Stop';
import { extendViewService } from 'tuc-webui-base/services/ViewService';
import ErrorComponent from 'grb-webui/components/ErrorComponent';
import { deepObjectDiffs } from 'grb-webui/services/diff';
import fromConfiguration from 'grb-webui/configuration/fromConfiguration';
import GraphDashboard from 'grb-webui/components/GraphDashboard';
import AppMainPage, { FIT_TO_PAGE_VARIANT } from '../../components/AppMainPage';
import { MAIN_JOBS_TYPE } from '../../ComponentTypes';
import JobLogDashboardTile from './JobLogDashboardTile';
import JobStatus from './JobStatus';
import withJobs from './withJobs';
import './JobDashboard.scss';

const JOB_TYPE = 'Job';

const sameJobs = (j1, j2) => {
  if (!j1) return !j2;
  if (!j2) return false;
  return !deepObjectDiffs(j1, j2);
  // return (j1.status === j2.status)
  //   && (j1.id === j2.id);
};

export default (options) => {
  const { getJobDashboardProps = () => {}, className } = options;
  const ViewServiceProperties = {
    componentsByName: {
      JobLogDashboardTile,
    },
  };

  //
  // JobDashboard component
  //
  const JobDashboard = (props) => {
    const {
      query,
      viewService,
      jobService,
    } = props;
    const { onAbort, detailsComponent, dashboardComponent, ...rest } = props;
    const { id } = query;
    const [extViewService] = useState(() => extendViewService(viewService, ViewServiceProperties));
    const [Tabs] = useState(() => fromConfiguration(detailsComponent, props));
    const [{ job, loading, error, showAbort, downloadURL }, setJob] = useState({ loading: true });

    useEffect(() => {
      const context = {};
      const loadJob = () => {
        return jobService.getJob(id).then((j) => {
          const abortable = jobService.jobStatusAbortable(j);
          if (!sameJobs(context.job, j)) {
            context.job = j;
            context.error = null;
            setJob({
              loading: false,
              job: j,
              downloadURL: jobService.getDownloadLogURL(j, options.jobType === MAIN_JOBS_TYPE),
              showAbort: abortable,
            });
          }
          // abortable === should continue polling
          context.polling = abortable ? setTimeout(loadJob, 500) : null;
        }).catch((e) => {
          if (!context.error) {
            context.error = e;
            setJob({
              loading: false,
              job: null,
              downloadURL: undefined,
              showAbort: false,
              error: e,
            });
          }
        });
      };
      if (id) {
        loadJob();
      }
      return () => {
        if (context.polling) {
          clearTimeout(context.polling);
        }
      };
    }, [id, jobService, viewService]);

    const handleAbort = useCallback(() => {
      onAbort(job);
    }, [job]);

    //
    // Header
    //
    const HeaderBar = (
      <div className="grb-main-page-header-buttons">
        {showAbort && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAbort}
            id="grb-abort-job-button"
            aria-label="Abort"
          >
            <AbortIcon className="grb-lefticon" />Abort
          </Button>
        )}
        {downloadURL && (
          <a href={downloadURL} download>
            <Button
              variant="outlined"
              className="grb-gutter-left"
              color="primary"
              id="grb-job-log-download"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="grb-lefticon MuiSvgIcon-root">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
              Download Log
            </Button>
          </a>
        )}
      </div>
    );

    return (
      <AppMainPage
        {...props}
        variant={FIT_TO_PAGE_VARIANT}
        Icon={job ? JobStatus({ jobService, job }) : undefined}
        pageTitle={`Log of job ${id}`}
        TrailingBar={HeaderBar}
        className={classnames('grb-job-log-page', className)}
        backTo
      >
        <div className={classnames('grb-job-log-content', 'grb-fullpage')}>
          {loading && (
            <div className="grb-loading-panel">
              <div>
                <Typography variant="h6">{`Loading job ${id}...`}</Typography>
                <div className="grb-progress-ctnr">
                  <LinearProgress />
                </div>
              </div>
            </div>
          )}
          {!!error && (
            <ErrorComponent title="Error accessing job log" error={error} />
          )}
          {!loading && !error && !!job && (
            <React.Fragment>
              <GraphDashboard
                {...rest}
                {...dashboardComponent}
                viewService={extViewService}
                job={job}
                jobId={id}
                elevation={2}
                {...getJobDashboardProps(job, props)}
              />
              <Paper
                className="grb-joblog-details"
                elevation={dashboardComponent.configuration && dashboardComponent.configuration.elevation || 0}
              >
                <Tabs
                  {...rest}
                  viewService={extViewService}
                  data={job}
                  type={JOB_TYPE}
                />
              </Paper>
            </React.Fragment>
          )}
        </div>
      </AppMainPage>
    );
  };

  JobDashboard.propTypes = {
    viewService: PropTypes.shape({
      onError: PropTypes.func,
      getComponent: PropTypes.func,
    }).isRequired,
    route: PropTypes.shape({}).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      state: PropTypes.shape({
        referrer: PropTypes.shape({}),
      }),
    }).isRequired,
    query: PropTypes.shape({
      id: PropTypes.string,
    }),
    jobService: PropTypes.shape({
      getDownloadLogURL: PropTypes.func,
      getJob: PropTypes.func,
      jobStatusAbortable: PropTypes.func,
    }).isRequired,
    detailsComponent: PropTypes.shape({}).isRequired,
    dashboardComponent: PropTypes.shape({
      configuration: PropTypes.shape({
        elevation: PropTypes.number,
      }),
    }).isRequired,
    onAbort: PropTypes.func.isRequired,
  };

  JobDashboard.defaultProps = {
    query: {},
  };

  return withJobs(JobDashboard, options);
};
